import { EmptyState } from "@/modules/common/components/ui-kit/empty-state";
import TrendUpIcon from "@/assets/icons/lead-page/trend-up";
import ActivityDetails from "@/modules/lead-page/new/components/activity/activity-details";
import { Box, Stack, useTheme, Zoom } from "@mui/material";
import useActivityTimeline from "@/modules/lead-page/new/hooks/activity-timline.hook";
import { ActivityItem } from "@/modules/lead-page/dtos/index-v2";
import ActivityDetailsSkeleton from "@/modules/lead-page/new/components/activity/activity-details-skeleton";
import React, { useEffect, useContext, useRef } from "react";
import { useSearchParams } from "react-router-dom";
import CommonIconButton from "@/modules/common/components/ui-kit/buttons/icon-button";
import ChevronIcon from "@/modules/common/components/ui-kit/icons/chevron";
import useScreenSize from "@/modules/common/hooks/device-detection.hook";
import { useActivityScrollContext } from "@/modules/lead-page/new/context/activity-scroll-context";
import ArchivedOverlay from "@/modules/common/components/ui-kit/archived-overlay";
import { useActivityContext } from "@/modules/lead-page/new/hooks/activity-context.hook";
import { LeadPageContext } from "@/modules/lead-page/context/lead-page.context";
import { scrollToElementWithRetry } from "@/modules/lead-page/new/utils/scroll.utils";
import { useFindInActivity } from "@/modules/lead-page/new/hooks/use-find-in-activity.hook";

type ActivityTabProps = {
  isHeaderCollapsed: boolean;
  onCollapseChange?: (collapsed: boolean) => void;
  addComment?: React.ReactNode;
  onNavigateToTrack?: (trackUId: string) => void;
};

export const ActivityTab: React.FC<ActivityTabProps> = ({
  addComment,
  onNavigateToTrack,
}) => {
  const theme = useTheme();
  const { isMobile } = useScreenSize();

  const {
    containerRef,
    loaderTopRef,
    loaderBottomRef,
    scrollToBottomWithRefresh,
    showScrollToBottom,
  } = useActivityScrollContext();

  const {
    allItems,
    isEmpty,
    isLoading,
    isError,
    isFetchingNextPage,
    isFetchingPreviousPage,
  } = useActivityTimeline();

  const { lead } = useContext(LeadPageContext);
  const isLeadArchived = lead?.data?.lead?.archived;

  const { highlightedActivityId, setHighlightedActivityId, isFindingActivity } =
    useActivityContext();
  const { findInActivity } = useFindInActivity();
  const [searchParams, setSearchParams] = useSearchParams();
  const activityIdFromUrl = searchParams.get("activityId");
  const timestampParam = searchParams.get("timestamp");
  const timestampFromUrl = (() => {
    if (!timestampParam) return null;
    const date = new Date(Number(timestampParam));
    return isNaN(date.getTime()) ? null : date.toISOString();
  })();
  const hasScrolledToUrlActivityRef = useRef(false);

  useEffect(() => {
    if (highlightedActivityId) {
      const timer = setTimeout(() => {
        setHighlightedActivityId?.(undefined);
      }, 5000);

      return () => clearTimeout(timer);
    }
  }, [highlightedActivityId, setHighlightedActivityId]);

  // Handle activityId from URL params (deep-link navigation)
  useEffect(() => {
    if (
      isLoading ||
      !timestampFromUrl ||
      !activityIdFromUrl ||
      hasScrolledToUrlActivityRef.current
    )
      return;

    hasScrolledToUrlActivityRef.current = true;
    findInActivity(activityIdFromUrl, timestampFromUrl);

    // Clean up URL params after triggering
    searchParams.delete("activityId");
    searchParams.delete("timestamp");
    setSearchParams(searchParams, { replace: true });
  }, [
    isLoading,
    searchParams,
    findInActivity,
    setSearchParams,
    timestampFromUrl,
    activityIdFromUrl,
  ]);

  // Scroll to highlighted activity - only once when it's first set
  const hasScrolledToHighlightRef = useRef<string | null>(null);
  const previousFindingStateRef = useRef<boolean>(false);

  // Reset the scroll ref when finding activity starts (allows re-scrolling to the same activity)
  useEffect(() => {
    if (isFindingActivity && !previousFindingStateRef.current) {
      // Finding just started, reset the ref to allow scrolling again
      hasScrolledToHighlightRef.current = null;
    }
    previousFindingStateRef.current = isFindingActivity || false;
  }, [isFindingActivity]);

  // Scroll to highlighted activity when it's set and data is loaded
  useEffect(() => {
    // Early returns for invalid states
    if (!highlightedActivityId || allItems.length === 0 || isFindingActivity) {
      // Reset the ref when highlight is cleared
      if (!highlightedActivityId) {
        hasScrolledToHighlightRef.current = null;
      }
      return;
    }

    // Skip if we've already scrolled to this activity
    if (hasScrolledToHighlightRef.current === highlightedActivityId) {
      return;
    }

    // Find the activity item by ID or UID
    const item = allItems.find(
      (i) =>
        `${i.id}-${i.uid}` === highlightedActivityId ||
        i.uid === highlightedActivityId,
    );

    if (!item) {
      return;
    }

    // Mark that we're about to scroll to this highlight
    hasScrolledToHighlightRef.current = highlightedActivityId;

    const elementId = `activity-${item.id}-${item.uid}`;

    // Use utility function to scroll to element with retry mechanism
    const cleanup = scrollToElementWithRetry(elementId, {
      initialDelay: 150,
      retryDelay: 100,
      maxRetries: 5,
      behavior: "smooth",
      block: "center",
    });

    // Return cleanup function to cancel pending scroll operations
    return cleanup;
  }, [highlightedActivityId, allItems, isFindingActivity]);

  const renderActivityContent = () => {
    if (isLoading) {
      return (
        <Stack direction="column" flex="1 0 0" spacing={theme.nawy.spacing.sm}>
          {[...Array(5)].map((_, index) => (
            <ActivityDetailsSkeleton key={index} isInitialLoad={isLoading} />
          ))}
        </Stack>
      );
    }
    if (isEmpty) {
      return (
        <Stack
          direction="column"
          flex="1 0 0"
          spacing={theme.nawy.spacing.sm}
          bgcolor={theme.nawy.colors.semantic.layer.primary}
        >
          <EmptyState
            icon={<TrendUpIcon />}
            label="No Activity Yet"
            description="This lead has no activity yet."
          />
        </Stack>
      );
    }
    if (isError) {
      return (
        <EmptyState
          icon={<TrendUpIcon />}
          label="Error Loading Activity"
          description="There was an error loading the activity for this lead."
        />
      );
    }

    return (
      <>
        {isFetchingNextPage &&
          [...Array(3)].map((_, index) => (
            <ActivityDetailsSkeleton key={index} isInitialLoad={false} />
          ))}
        <Stack direction="column">
          <Box ref={loaderTopRef} sx={{ height: 20 }} />

          <ArchivedOverlay isArchived={isLeadArchived}>
            {[...allItems].reverse().map((item: ActivityItem) => {
              const isHighlighted =
                highlightedActivityId === `${item?.id}-${item.uid}` ||
                highlightedActivityId === item.uid;

              return (
                <Box
                  key={`${item?.id}-${item.uid}`}
                  id={`activity-${item?.id}-${item.uid}`}
                  className="activity-item"
                  sx={{
                    transition: "background-color 0.3s ease-out",
                    backgroundColor: isHighlighted
                      ? theme.nawy.colors.primitives.blue[200]
                      : "transparent",
                    borderRadius: theme.nawy.radius.md,
                    animation: isHighlighted
                      ? "highlightFlash 1.2s ease-in-out 3"
                      : "none",
                    "@keyframes highlightFlash": {
                      "0%, 100%": {
                        opacity: 1,
                      },
                      "50%": {
                        opacity: 0.75,
                      },
                    },
                  }}
                >
                  <ActivityDetails
                    item={item}
                    onNavigateToTrack={onNavigateToTrack}
                  />
                </Box>
              );
            })}
          </ArchivedOverlay>

          <Box ref={loaderBottomRef} sx={{ height: 20 }} />
        </Stack>
        {isFetchingPreviousPage &&
          [...Array(3)].map((_, index) => (
            <ActivityDetailsSkeleton
              key={`bottom-${index}`}
              isInitialLoad={false}
            />
          ))}
      </>
    );
  };

  return (
    <Stack
      direction="column"
      maxWidth="100%"
      height="100%"
      boxSizing="border-box"
      sx={{ overflow: "hidden", position: "relative" }}
    >
      <Box
        ref={containerRef}
        sx={{
          display: "flex",
          flexDirection: "column",
          height: "100%",
          overflowY: "auto",
          WebkitOverflowScrolling: "touch",
        }}
      >
        {renderActivityContent()}
      </Box>

      <ArchivedOverlay isArchived={isLeadArchived}>
        <Box sx={{ position: "relative", zIndex: 2 }}>{addComment}</Box>
      </ArchivedOverlay>
      <Zoom in={showScrollToBottom} unmountOnExit>
        <CommonIconButton
          size="sm"
          variant="outlined-secondary"
          onClick={scrollToBottomWithRefresh}
          sx={{
            position: "absolute",
            bottom: isMobile ? 70 : 100,
            right: theme.nawy.spacing.sm,
            zIndex: 3,
          }}
        >
          <ChevronIcon />
        </CommonIconButton>
      </Zoom>
    </Stack>
  );
};

export default ActivityTab;
